// @ts-strict-ignore
import { useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, SearchBar, Link, Typography, Spacer, Message } from "@aviary";
import { ProductImage, AvailabilityPill } from "@aviary/molecules";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import {
  type EmersonProductSearchResult,
  useSkuProductLazyQuery,
} from "@emerson/data/queries/SkuProductQuery/SkuProduct.query";
import { l } from "@emerson/locales/i18n";
import { BrandName } from "@shared/components/ProductCard/BrandName/BrandName";
import { ProductName } from "@shared/components/ProductCard/ProductName/ProductName";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import {
  EmersonCheckoutAddToCartEntryPoint,
  EmersonCheckoutStartEntryPoint,
  EmersonViewProductEntryPoint,
} from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import { BrandAgreementEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import type { AvailabilityStatusValueType } from "@shared/types/availabilityStatus";
import { Size } from "@shared/types/sizes";

import { AddToCartButton } from "../AddToCartButton/AddToCartButton";
import { PerUnitAmount } from "../PerUnitAmount/PerUnitAmount";

import * as styles from "./QuickOrder.styles";

enum ActionTypes {
  ADDED_TO_CART = "ADDED_TO_CART",
  NOT_FOUND = "NOT_FOUND",
}

interface Props {
  setIsModalOpen: (value: boolean) => void;
}

const QuickOrder = ({ setIsModalOpen }: Props) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalyticsLogger();

  const [query, setQuery] = useState<string>("");
  const [product, setProduct] = useState<EmersonProductSearchResult>(null);
  const [actionStatus, setActionStatus] = useState<ActionTypes | null>(null);
  const [resetQuantity, setResetQuantity] = useState<boolean>(false);
  const [executeSearch] = useSkuProductLazyQuery();
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setActionStatus(null);
    setQuery(e.target.value);
  };

  const handleSearch = (queryString: string) => {
    onSearch(queryString);
  };

  const handleTrailingIconClick = () => {
    setActionStatus(null);
    setQuery("");
  };

  const {
    id,
    sku,
    descriptor,
    emersonVariantId,
    wholesalePrice,
    availabilityStatus,
    imageNew,
    expectedReturnDate,
    // TODO: update query after promotions are ready
    wholesalePromotionDetails,
  } = product?.variants?.[0] || {};

  const to = emersonRoutes.getPDP(
    product?.brand?.name,
    product?.slug,
    product?.id,
    emersonVariantId
  );

  const onComplete = () => {
    setActionStatus(ActionTypes.ADDED_TO_CART);
    setQuery("");
    setProduct(null);
    setResetQuantity(true);
    setIsModalOpen(false);
  };

  const onClickAddToCart = () => {
    setIsModalOpen(true);
  };

  const onSearch = async queryString => {
    if (queryString === "") {
      setProduct(null);

      return;
    }

    const [skuResult, emersonVariantIdResult] = await Promise.all([
      executeSearch({
        variables: { filters: { sku: queryString }, variantFilters: { query: queryString } },
      }),
      executeSearch({
        variables: {
          filters: { emersonVariantId: queryString },
          variantFilters: { query: queryString },
        },
      }),
    ]);

    const skuProduct = skuResult.data.viewer.productSearchV2.nodes[0];
    const emersonVariantIdData = emersonVariantIdResult.data.viewer.productSearchV2.nodes[0];

    const result = skuProduct || emersonVariantIdData;
    setProduct(result);

    if (result || product) setActionStatus(null);

    setTimeout(() => {
      !result && !product ? setActionStatus(ActionTypes.NOT_FOUND) : setActionStatus(null);
    }, 500);
  };

  const logProductViewEvent = () => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_PRODUCT_VIEW,
      payload: {
        entry_point: EmersonViewProductEntryPoint.QUICK_ORDER,
        default_variant: sku,
        product_status: availabilityStatus?.toLowerCase(),
        sku,
        brand: product?.brand?.name,
        search_rank: 1,
      },
    });
  };

  const renderAddToCartButton = () => (
    <AddToCartButton
      resetQuantity={resetQuantity}
      variantId={id}
      sku={sku}
      brandName={product?.brand?.name}
      initialQuantity={1}
      defaultVariant={sku}
      containsHemp={product?.containsCbd}
      availabilityStatus={availabilityStatus || "unavailable"}
      withQuantitySelectorMargin={false}
      buttonSize={Size.LARGE}
      analyticsEntryPoint={EmersonCheckoutStartEntryPoint.QUICK_ORDER}
      entryPoint={EmersonCheckoutAddToCartEntryPoint.QUICK_ORDER}
      brandAgreementEntryPoint={BrandAgreementEntryPoint.QUICK_ORDER}
      isPrimary
      isQuickOrder
      showQuantitySelector
      overrideButtonWrapper
      isAddedButtonTextOnly
      onAddedToCart={onComplete}
      onClickAddToCart={onClickAddToCart}
    />
  );

  const renderProduct = () => {
    if (!product) {
      return null;
    }

    return (
      <>
        <Spacer height="one" />
        <Arrange justify="spaceBetween" alignment="center" isWrapped>
          <Arrange isWrapped>
            <Arrange justify="start" isFullHeight isVertical>
              <Link to={to} data-testid="img-to-pdp">
                <ProductImage
                  image={imageNew}
                  name={product.name}
                  size={Size.XSMALL}
                  availabilityStatus={availabilityStatus as AvailabilityStatusValueType}
                />
              </Link>
            </Arrange>
            <Arrange isVertical spacing="none" css={styles.productInfo}>
              <Link
                css={styles.link}
                to={to}
                onClick={logProductViewEvent}
                data-testid="go-to-pdp-button"
                data-e2e="go-to-pdp"
              >
                <ProductName name={product.name} type="h5" wordWrap />
              </Link>
              <BrandName name={product.brand?.name} />
              <Typography isMarginless>{descriptor}</Typography>
              <AvailabilityPill
                availabilityStatus={availabilityStatus as AvailabilityStatusValueType}
                expectedReturnDate={expectedReturnDate}
                size={Size.SMALL}
              />
            </Arrange>
          </Arrange>
          <div>
            <PerUnitAmount promoTotal={wholesalePromotionDetails?.price} amount={wholesalePrice} />
          </div>
        </Arrange>
      </>
    );
  };

  const renderMessage = () => {
    if (!actionStatus) return null;

    if (actionStatus) {
      switch (actionStatus) {
        case ActionTypes.ADDED_TO_CART:
          return (
            <Message css={styles.message} isColor="success">
              {t(l.quickOrder.addedToCart)}
            </Message>
          );
        case ActionTypes.NOT_FOUND:
          return (
            <Message css={styles.message} isColor="danger">
              {t(l.quickOrder.notFound)}
            </Message>
          );
      }
    }
  };

  return (
    <div data-testid="emerson-cart-body" css={styles.quickOrderPopup}>
      <Arrange isWrapped={false} isFullWidth css={styles.container}>
        <SearchBar
          incrementalSearch
          boxStyles={styles.searchBox}
          onChange={handleChange}
          onSearch={handleSearch}
          onTrailingIconClick={handleTrailingIconClick}
          placeholder={t(l.quickOrder.searchPlaceholder)}
          value={query}
        />
        {renderAddToCartButton()}
      </Arrange>
      {renderProduct()}
      {!!actionStatus && <Spacer height="one" />}
      {renderMessage()}
    </div>
  );
};

export { QuickOrder };
